/**
 * Custom QueryBuilder & PredicateBuilder
 *
 * Usage:
 *
 *   import { QueryBuilder, PredicateBuilder, executeQuery } from "@Services/queryBuilder";
 *   const q = new QueryBuilder().where(new PredicateBuilder("name","contains","x").or("id","contains","x")).take(3);
 *   const result = await executeQuery(url, q);
 */

import axiosWrapper from "@AxiosWrapper";

// ─── Operator type ───────────────────────────────────────────────────
export type QueryOperator =
  | "equal"
  | "notequal"
  | "lessthan"
  | "greaterthan"
  | "lessthanorequal"
  | "greaterthanorequal"
  | "contains"
  | "startswith"
  | "endswith";

// ─── PredicateBuilder ────────────────────────────────────────────────
export interface PredicateShape {
  field: string;
  operator: QueryOperator;
  value: unknown;
  isComplex?: boolean;
  ignoreCase?: boolean;
  ignoreAccent?: boolean;
  condition?: "and" | "or";
  predicates?: PredicateShape[];
}

/**
 * Chainable builder that mirrors Syncfusion Predicate's API.
 * Produces a plain object (PredicateShape) consumable by the backend.
 */
export class PredicateBuilder implements PredicateShape {
  field: string;
  operator: QueryOperator;
  value: unknown;
  isComplex: boolean;
  ignoreCase: boolean;
  ignoreAccent: boolean;
  condition?: "and" | "or";
  predicates?: PredicateShape[];

  constructor(field: string, operator: QueryOperator, value: unknown, ignoreCase = true) {
    this.field = field;
    this.operator = operator;
    this.value = value;
    this.isComplex = false;
    this.ignoreCase = ignoreCase;
    this.ignoreAccent = false;
  }

  /**
   * Adds an OR condition. Can take (field, operator, value) or another PredicateBuilder.
   */
  or(fieldOrPredicate: string | PredicateBuilder, operator?: QueryOperator, value?: unknown): PredicateBuilder {
    const other =
      fieldOrPredicate instanceof PredicateBuilder
        ? fieldOrPredicate
        : new PredicateBuilder(fieldOrPredicate, operator!, value);

    const combined = new PredicateBuilder(this.field, this.operator, this.value, this.ignoreCase);
    combined.isComplex = true;
    combined.condition = "or";
    combined.predicates = [_toShape(this), _toShape(other)];
    return combined;
  }

  /**
   * Adds an AND condition. Can take (field, operator, value) or another PredicateBuilder.
   */
  and(fieldOrPredicate: string | PredicateBuilder, operator?: QueryOperator, value?: unknown): PredicateBuilder {
    const other =
      fieldOrPredicate instanceof PredicateBuilder
        ? fieldOrPredicate
        : new PredicateBuilder(fieldOrPredicate, operator!, value);

    const combined = new PredicateBuilder(this.field, this.operator, this.value, this.ignoreCase);
    combined.isComplex = true;
    combined.condition = "and";
    combined.predicates = [_toShape(this), _toShape(other)];
    return combined;
  }

  /** Serialise to a plain object (strips class prototype). */
  toJSON(): PredicateShape {
    return _toShape(this);
  }

  /** Alias matching Syncfusion's Predicate.toJson() (lowercase j). */
  toJson(): PredicateShape {
    return _toShape(this);
  }
}

/** Convert a PredicateBuilder (or PredicateShape) to a plain object. */
function _toShape(p: PredicateShape): PredicateShape {
  const shape: PredicateShape = {
    field: p.field,
    operator: p.operator,
    value: p.value,
    isComplex: p.isComplex ?? false,
    ignoreCase: p.ignoreCase ?? true,
    ignoreAccent: p.ignoreAccent ?? false,
  };
  if (p.condition) shape.condition = p.condition;
  if (p.predicates?.length) shape.predicates = p.predicates.map(_toShape);
  return shape;
}

// ─── QueryBuilder ────────────────────────────────────────────────────
export interface QueryShape {
  where?: PredicateShape[];
  skip?: number;
  take?: number;
  requiresCounts?: boolean;
  sorted?: { name: string; direction: "ascending" | "descending" }[];
  search?: string;
  select?: string[];
  [key: string]: unknown;
}

/**
 * Chainable builder that mirrors Syncfusion Query's API.
 * Produces a plain object (QueryShape) matching backend expectations.
 *
 * All methods return `this` for chaining.
 */
export class QueryBuilder {
  private _where: PredicateShape[] = [];
  private _skip?: number;
  private _take?: number;
  private _requiresCounts = false;
  private _sorted: { name: string; direction: "ascending" | "descending" }[] = [];
  private _search?: string;
  private _select?: string[];
  private _params: Record<string, unknown> = {};

  /**
   * Add a where clause.
   * Accepts either:
   *   .where("field", "operator", value)
   *   .where(predicateBuilder)
   */
  where(fieldOrPredicate: string | PredicateBuilder, operator?: QueryOperator, value?: unknown): this {
    if (fieldOrPredicate instanceof PredicateBuilder) {
      this._where.push(_toShape(fieldOrPredicate));
    } else {
      this._where.push({
        field: fieldOrPredicate,
        operator: operator!,
        value,
        isComplex: false,
        ignoreCase: true,
        ignoreAccent: false,
      });
    }
    return this;
  }

  /** Set skip (pagination offset). */
  skip(n: number): this {
    this._skip = n;
    return this;
  }

  /** Set take (page size). */
  take(n: number): this {
    this._take = n;
    return this;
  }

  /** Flag the query to return total count alongside results. */
  requiresCount(): this {
    this._requiresCounts = true;
    return this;
  }

  /** Add a sort criterion. */
  sortBy(field: string, direction: "ascending" | "descending" = "ascending"): this {
    this._sorted.push({ name: field, direction });
    return this;
  }

  /** Set a search string. */
  search(value: string): this {
    this._search = value;
    return this;
  }

  /** Select specific fields. */
  select(fields: string[]): this {
    this._select = fields;
    return this;
  }

  /**
   * Add an arbitrary parameter (equivalent to Syncfusion's Query.addParams).
   * These get spread as top-level keys in the serialised payload.
   */
  addParams(key: string, value: unknown): this {
    this._params[key] = value;
    return this;
  }

  /**
   * Build the final plain object.
   * The shape matches what Syncfusion's WebMethodAdaptor sends and what
   * the backend / SlickGrid1 expects.
   */
  build(): QueryShape {
    const result: QueryShape = { ...this._params };

    if (this._where.length) result.where = this._where;
    if (this._skip !== undefined) result.skip = this._skip;
    if (this._take !== undefined) result.take = this._take;
    if (this._requiresCounts) result.requiresCounts = true;
    if (this._sorted.length) result.sorted = this._sorted;
    if (this._search !== undefined) result.search = this._search;
    if (this._select) result.select = this._select;

    return result;
  }

  /**
   * Automatically called by JSON.stringify and object spread.
   * Allows `{ ...queryBuilder }` and `JSON.stringify(queryBuilder)` to work.
   */
  toJSON(): QueryShape {
    return this.build();
  }
}

// ─── executeQuery (replaces DataManager.executeQuery) ────────────────
interface ExecuteQueryResult<T = unknown> {
  result: T[];
  count?: number;
  actual?: { result: T[]; count?: number };
}

/**
 * Replaces `dataManagerService.executeQuery(syncfusionQuery)`.
 *
 * Sends a POST to the given URL with { value: queryShape }, matching
 * the exact payload shape Syncfusion's WebMethodAdaptor produces.
 */
export async function executeQuery<T = unknown>(
  url: string,
  query: QueryBuilder | QueryShape,
  signal?: AbortSignal,
): Promise<ExecuteQueryResult<T>> {
  const value = query instanceof QueryBuilder ? query.build() : query;
  const response = await axiosWrapper.post<ExecuteQueryResult<T>>(url, { value }, { signal });
  return response.data;
}

// ─── DataUtil replacement ────────────────────────────────────────────

/**
 * Groups an array of objects by a column name.
 * Drop-in replacement for Syncfusion's `DataUtil.group(data, columnName)`.
 */
export function groupBy<T = unknown>(data: T[], columnName: string): T[] {
  const groups: Record<string, T[]> = {};
  for (const item of data) {
    // `columnName` is a runtime-chosen field, so the row is read through a dynamic-key view of T.
    const key = String((item as T & Record<string, unknown>)[columnName] ?? "");
    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
  }
  return Object.values(groups).flat();
}
